import * as Sentry from "@sentry/react";
import { Capacitor } from "@capacitor/core";
import { StatusBar, Style } from "@capacitor/status-bar";
import { App as CapacitorApp } from "@capacitor/app";
import { createRoot } from "react-dom/client";
import App from "./App";
import { getAuthToken, hydrateAuthTokenFromNativeStorage } from "./lib/apiBase";
import {
  initAppHistory,
  startAppHistoryListener,
  appBack,
} from "./lib/appHistory";
import { setupIosEdgeSwipe } from "./lib/iosEdgeSwipe";
import "./index.css";

const APP_HEADER_BLUE = "#0d2461";

async function configureNativeStatusBar() {
  try {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== "android") return;
    await StatusBar.setOverlaysWebView({ overlay: false });
    await StatusBar.setBackgroundColor({ color: APP_HEADER_BLUE });
    await StatusBar.setStyle({ style: Style.Dark });
  } catch {
  }
}

// ─── Native back-button bridge ──────────────────────────────────────────────
// Capacitor 8 routes Android hardware back through `@capacitor/app`'s
// `backButton` event. We forward every press to `appBack()` — the single
// back entry-point that also serves the iOS edge-swipe gesture (see
// iosEdgeSwipe.ts) and visible back buttons (Header, ListingDetail,
// Messages, …). `appBack` is responsible for:
//   1. Letting MobileCategoryDrillDown consume the press if its overlay
//      is mounted with internal levels to pop (window.__iraqsooqDrillBack).
//   2. Arming the drill-restore marker if the user is leaving a
//      /category/<key> page so Home / MobileCategories re-opens the
//      drill at the exact branch.
//   3. Popping the deterministic logical app-history stack (see
//      appHistory.ts) instead of WKBackForwardList, which on TestFlight
//      proved unreliable due to documented WebKit sync bugs with
//      pushState/replaceState.
//   4. At logical root: exit the app on Android, no-op on iOS (matches
//      iOS Safari edge-swipe-at-root semantics).
//
// Safety: registered only when `Capacitor.isNativePlatform()`; web is
// completely unaffected.
function registerNativeBackButtonHandler() {
  try {
    if (!Capacitor.isNativePlatform()) return;
    CapacitorApp.addListener("backButton", () => {
      appBack();
    });
  } catch {
  }
}

// ─── Native (Capacitor) fetch shim ───────────────────────────────────────────
// In native builds the SPA is loaded from capacitor://localhost (iOS) or
// https://localhost (Android), so any `fetch("/api/...")` would resolve to
// that local origin and 404 — there is no co-located server in the WebView.
//
// `client/src/lib/queryClient.ts` already routes its own calls through
// `buildApiUrl()`, but the codebase still has many direct `fetch("/api/...")`
// callsites in pages/components that were written for the browser. Patching
// every call site is a large, risky refactor that this phase explicitly
// avoids.
//
// Instead: at app boot — and ONLY when `VITE_API_BASE_URL` is set (i.e. the
// build is intended for the bundled native shell) — we install a single
// global fetch wrapper that prefixes any string URL starting with `/api/`,
// `/uploads/`, or `/static/` with the configured remote origin. Web builds
// leave `VITE_API_BASE_URL` empty, so the wrapper is never installed and
// behavior is byte-identical to before.
//
// Safety properties:
//   - No-op when `VITE_API_BASE_URL` is empty (web build path).
//   - Only rewrites string URLs whose pathname matches an allow-list of
//     server-owned prefixes — never touches third-party URLs (Cloudinary,
//     OpenStreetMap tiles, etc.) or already-absolute http(s)://… URLs.
//   - Adds `credentials: "include"` only if the caller didn't specify one,
//     so cross-origin cookies/CORS continue to work for the auth session.
//   - Adds Authorization only to `/api/*` calls when a native bearer token
//     exists, letting direct fetch callsites work in Capacitor without cookies.
//   - `Request` objects (used by some libraries) are passed through
//     unchanged — they already carry an absolute URL by spec.
try {
  const configuredApiBase = (import.meta.env.VITE_API_BASE_URL ?? "").toString().trim().replace(/\/+$/, "");
  // On a native shell the bundled app has no co-located server, so it MUST
  // know the public backend origin. If the build didn't inject
  // VITE_API_BASE_URL, fall back to the production origin instead of leaving
  // the shim uninstalled (which left every /api/ call relative → the app
  // showed no products/stores). On web this resolves to "" and the shim is
  // never installed, so web behavior is byte-identical to before.
  const apiBase = configuredApiBase || (Capacitor.isNativePlatform() ? "https://iraq-sooq.com" : "");
  if (apiBase && typeof window !== "undefined" && typeof window.fetch === "function") {
    const REWRITE_PREFIXES = ["/api/", "/uploads/", "/static/"];
    const originalFetch = window.fetch.bind(window);
    window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
      if (typeof input === "string" && REWRITE_PREFIXES.some((p) => input.startsWith(p))) {
        const rewritten = `${apiBase}${input}`;
        const nextInit: RequestInit = {
          ...(init || {}),
          credentials: init?.credentials !== undefined ? init.credentials : "include",
        };
        if (input.startsWith("/api/")) {
          // Always advertise the native-client identity on /api/* calls.
          // The server's CSRF gate (server/middleware/csrf.ts) lets
          // requests carrying `X-Client: native` through the same way
          // it lets Bearer-token requests through, because the
          // Capacitor shells (Android `https://localhost`, iOS
          // `capacitor://localhost`) can never participate in a
          // cookie-CSRF attack: their origin differs from the API
          // origin, so the browser strips the session cookie before
          // the request even leaves the device. Without this header,
          // the very first POST /api/auth/login on a fresh install
          // (no Bearer token yet) was rejected with 403 and broke
          // login on Android.
          const headers = new Headers(init?.headers);
          if (!headers.has("X-Client")) {
            headers.set("X-Client", "native");
          }
          const token = getAuthToken();
          if (token && !headers.has("Authorization")) {
            headers.set("Authorization", `Bearer ${token}`);
          }
          nextInit.headers = headers;
        }
        return originalFetch(rewritten, nextInit);
      }
      return originalFetch(input as any, init);
    }) as typeof window.fetch;
  }
} catch {
}

try {
  if (import.meta.env.VITE_SENTRY_DSN) {
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN as string,
      environment: import.meta.env.MODE,
      tracesSampleRate: 0.2,
      integrations: [Sentry.browserTracingIntegration()],
    });
  }
} catch {
}

const CHUNK_RELOAD_KEY = "__chunk_reload";
function handleChunkError() {
  try {
    const lastReload = sessionStorage.getItem(CHUNK_RELOAD_KEY);
    const now = Date.now();
    if (lastReload && now - parseInt(lastReload) < 30000) return;
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(now));
  } catch {
    return;
  }
  window.location.reload();
}

window.addEventListener("vite:preloadError", handleChunkError);

window.addEventListener("error", (event) => {
  const msg = event.message || "";
  if (
    msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("Importing a module script failed")
  ) {
    handleChunkError();
  }
});

// ─── Mobile keyboard "focused-field-visibility" helper ───────────────────────
// Why this exists:
//   On Android Capacitor (and iOS to a lesser extent), tapping a form field
//   that sits below the fold causes the on-screen keyboard to slide up and
//   cover the field — the user can't see what they're typing without
//   manually scrolling the page. The OS does NOT always auto-scroll a
//   focused element into view, especially when the input lives inside a
//   nested scroll container (cards, dialogs, fixed-bottom layouts), and
//   when it does, the timing is off.
//
//   This is a pure scroll-into-view helper, not an auth or layout change:
//   when any input/textarea/select gains focus, we wait one animation
//   frame (~250ms) for the keyboard to slide in and the WebView viewport
//   to resize, then nudge the focused element to the vertical center of
//   the visible area. If the field is already visible the browser does
//   nothing; if it's not, the page scrolls just enough to reveal it.
//
// Safety:
//   - Only listens to `focusin` (passive, capturing) — no key/value
//     intercept, no input rewriting, no auth-state coupling.
//   - Wrapped in try/catch; failures degrade silently to "no auto-scroll".
//   - Active on web AND native: same UX everywhere, simpler to reason
//     about than a Capacitor-only branch.
function setupKeyboardScrollIntoView() {
  try {
    if (typeof document === "undefined") return;
    document.addEventListener(
      "focusin",
      (event) => {
        const target = event.target as HTMLElement | null;
        if (!target) return;
        const tag = target.tagName;
        const isFormField =
          tag === "INPUT" ||
          tag === "TEXTAREA" ||
          tag === "SELECT" ||
          target.isContentEditable;
        if (!isFormField) return;
        // Defer: virtual keyboards animate over ~200-300ms and the WebView
        // viewport (and any 100dvh-bound containers) resize during that
        // animation. Scrolling immediately would target the pre-keyboard
        // layout and the input would slide right back behind the keyboard.
        window.setTimeout(() => {
          try {
            target.scrollIntoView({ block: "center", behavior: "smooth" });
          } catch {
            // ignore — best-effort UX nudge, never fatal
          }
        }, 250);
      },
      true,
    );
  } catch {
    // never let setup throw — UX helper only
  }
}
setupKeyboardScrollIntoView();

/**
 * Native launch surface: the phone apps open on the categories browse, not
 * the flat feed — the Sahibinden pattern this mobile UX is modelled on. The
 * "الرئيسية" tab keeps showing the products feed (it plays Sahibinden's
 * "Vitrin" role), so nothing becomes unreachable; only the screen you land
 * on at launch changes.
 *
 * Runs BEFORE initAppHistory() so the logical back stack is seeded with
 * `/categories` as its single root and the home feed never flashes.
 *
 * Deliberately narrow:
 *   - native only — the website's landing page is untouched;
 *   - only a cold launch at the bare app root. A deep link (push
 *     notification, shared listing URL, query string) keeps its own path.
 */
function applyNativeLaunchRoute(): void {
  if (typeof window === "undefined") return;
  if (!Capacitor.isNativePlatform()) return;
  const { pathname, search, hash } = window.location;
  if (search || hash) return;
  if (pathname !== "/" && pathname !== "/index.html") return;
  window.history.replaceState(null, "", "/categories");
}

const container = document.getElementById("root");
if (!container) {
  document.body.innerHTML = '<div style="padding:2rem;color:red;font-family:monospace">Error: #root element not found in DOM</div>';
} else {
  // Logical app-history layer. Order matters:
  //   1. initAppHistory: seed the sessionStorage stack with the current URL
  //      (or reset it if a stale stack from a previous foreground session
  //      doesn't match the URL we're rendering).
  //   2. startAppHistoryListener: install once-only push/replace/popstate
  //      listeners that mirror every wouter navigation into the stack.
  //   3. setupIosEdgeSwipe: install the iOS-only custom edge-swipe handler.
  //      No-op on web and Android. On iOS this REPLACES WKWebView's native
  //      `allowsBackForwardNavigationGestures`, which is set to NO in
  //      AppDelegate.swift / Main.storyboard for this same reason.
  //   4. registerNativeBackButtonHandler: forward Capacitor `backButton`
  //      to the same `appBack()` so all back surfaces share one logical back.
  applyNativeLaunchRoute();
  initAppHistory();
  startAppHistoryListener();
  setupIosEdgeSwipe();
  registerNativeBackButtonHandler();
  // Pull the Bearer token out of native persistent storage (UserDefaults
  // on iOS, SharedPreferences on Android) into localStorage BEFORE the
  // first render. The synchronous getAuthToken() reads from localStorage
  // only, so without this step the very first /api/auth/user fetch on
  // app launch goes out without an Authorization header and the user
  // sees the login screen — exactly the symptom reported on iOS after
  // closing-and-reopening the app.
  configureNativeStatusBar()
    .finally(() => hydrateAuthTokenFromNativeStorage())
    .finally(() => {
    const root = createRoot(container);
    root.render(
      <Sentry.ErrorBoundary
        fallback={
          <div style={{ padding: "2rem", textAlign: "center", direction: "rtl" }}>
            <h2>حدث خطأ غير متوقع</h2>
            <p>يرجى تحديث الصفحة أو التواصل معنا إذا استمرت المشكلة.</p>
            <button onClick={() => window.location.reload()}>تحديث الصفحة</button>
          </div>
        }
      >
        <App />
      </Sentry.ErrorBoundary>,
    );
  });
}
